<template>
  <template v-if="version === 'v2'">
    <div class="form-group form-group-v2" :class="classList">
      <label v-if="$slots.label || props.label" :class="labelClassList" :for="key">
        <template v-if="$slots.label">
          <slot name="label"/>
        </template>
        <template v-else>
          {{ props.label }}
          <span v-if="props.required" class="form-group-asterisk">*</span>
        </template>
      </label>

      <div class="form-group__content">
        <slot/>
        <template v-if="$slots.hint">
          <div :class="hintClassList"><slot name="hint"/></div>
        </template>
        <template v-if="props.hint">
          <div :class="hintClassList">{{ props.hint }}</div>
        </template>
        <template v-if="props.errorName || errorsSource">
          <s-error
            :error-name="props.errorName"
            :errors="errorsSource"
            :text-class-list="hintClassList"
          />
        </template>
      </div>
    </div>
  </template>
  <template v-else>
    <div class="form-group" :class="classList">
      <label v-if="$slots.label || props.label" :class="labelClassList" :for="key">
        <template v-if="$slots.label">
          <slot name="label"/>
        </template>
        <template v-else>
          {{ props.label }}
          <span v-if="props.required" class="form-group-asterisk">*</span>
        </template>
      </label>

      <div class="form-group__content">
        <slot/>
        <template v-if="$slots.hint">
          <span class="form-text"><slot name="hint"/></span>
        </template>
        <template v-if="props.hint">
          <span class="form-text text-muted">{{ props.hint }}</span>
        </template>
        <template v-if="props.errorName || errorsSource">
          <s-error :error-name="props.errorName" :errors="errorsSource"/>
        </template>
      </div>
    </div>
  </template>
</template>

<script setup>
  import Ids from '@/utils/ids';
  import { computed, getCurrentInstance, inject, toRef } from 'vue';
  import { useFieldErrors } from '@/composables/form/field-errors';

  const ID = new Ids(`form-group-${getCurrentInstance().uid}`);

  const props = defineProps({
    version: {
      type: String,
      default: '1',
    },
    label: {
      type: String,
      default: null,
    },
    required: {
      type: Boolean,
      default: false,
    },
    for: {
      type: String,
      default: null,
    },
    contentClass: {
      type: String,
      default: null,
    },
    hint: {
      type: String,
      default: null,
    },
    errorName: {
      type: [String, Array],
      default: null,
    },
    labelTextSize: {
      type: String,
      default: 'base',
    },
  });

  const formOptions = inject('formOptions', null);

  const key = props.for || ID.nextPositive();
  const { errorsSource, hasError } = useFieldErrors(formOptions?.formOptions?.fieldErrors, toRef(() => props.errorName));

  const labelClassList = computed(() => {
    if (props.version === 'v2') {
      return [`v2-text--${ props.labelTextSize }`, 'mb-tiny-2', 'text-v2-secondary'];
    }

    return ['form-group__label'];
  });

  const hintClassList = computed(() => {
    const list = [getHintTextSize(props.labelTextSize), 'mt-tiny-2', 'text-v2-secondary'];

    if (hasError.value) {
      list.push('text-v2-danger');
    }

    return list;
  });

  const classList = computed(() => {
    const list = [];

    if (hasError.value) {
      list.push('form-group--has-error');
    }

    return list;
  });

  function getHintTextSize(size) {
    switch (size) {
      case 'base':
        return 'v2-text--small';
      case 'small':
        return 'v2-text--mini';
      case 'mini':
      case 'tiny':
        return 'v2-text--tiny';
      default:
        return 'v2-text--small';
    }
  }
</script>
